/* eslint-disable no-undef */
// DOM type declaration for web builds; not used on native
declare const document: any;

import React, { useCallback } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  StyleSheet,
  Dimensions,
  Linking,
  Platform,
  Share,
  Alert,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { EVENTS_COLORS } from '@/constants/residents/mobile/eventsColors';
import { SVG_COMPONENTS } from '@/assets/admin';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { Event } from '@/types/residents/event';
import { buildGoogleCalendarUrl, buildICS } from '@/utils/residents/events/calendar';
import { formatEventDetailsForSharing } from '@/utils/residents/events/eventHelpers';

const CloseIcon = SVG_COMPONENTS.CANCEL_X;
const CalendarIcon = RESIDENTS_SVG_COMPONENTS.CALENDAR;
const AppleIcon = RESIDENTS_SVG_COMPONENTS.APPLE;

const { width: SCREEN_WIDTH } = Dimensions.get('window');

interface AddToCalendarBottomSheetProps {
  visible: boolean;
  onClose: () => void;
  event: Event;
}

/**
 * Bottom sheet modal for adding events to calendar
 * Matches Figma design: 1.b. Add to calendar
 */
export const AddToCalendarBottomSheet: React.FC<AddToCalendarBottomSheetProps> = ({
  visible,
  onClose,
  event,
}) => {
  const insets = useSafeAreaInsets();

  // Open Google Calendar
  const handleGoogleCalendar = useCallback(() => {
    const url = buildGoogleCalendarUrl(event);
    Linking.openURL(url).catch((err) => {
      console.error('Failed to open Google Calendar:', err);
    });
    onClose();
  }, [event, onClose]);

  // Download ICS file (for Outlook and Apple Calendar)
  const handleDownloadICS = useCallback(async () => {
    const ics = buildICS(event);
    const fileName = `${(event.name || 'event').replace(/\s+/g, '_')}.ics`;
    
    if (Platform.OS === 'web') {
      // Web: Create download link
      try {
        const dataUri = `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
        const a = document.createElement('a');
        a.href = dataUri;
        a.setAttribute('download', fileName);
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (error) {
        console.error('Failed to download ICS:', error);
      }
    } else {
      // Native: Use Share API since data URIs aren't supported by Linking
      // This lets users copy event details or share to other apps
      try {
        const eventDetails = formatEventDetailsForSharing(event);
        await Share.share({
          message: eventDetails,
          title: event.name || 'Event',
        });
      } catch (error) {
        console.error('Failed to share event:', error);
        Alert.alert(
          'Add to Calendar',
          'To add this event to your calendar, please manually create an event with these details:\n\n' +
          `Event: ${event.name}\n` +
          `Date: ${event.startTime ? new Date(event.startTime).toLocaleDateString() : 'TBD'}\n` +
          `Location: ${event.location || 'TBD'}`,
          [{ text: 'OK' }]
        );
      }
    }
    onClose();
  }, [event, onClose]);

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <TouchableOpacity
          style={styles.backdrop}
          onPress={onClose}
          activeOpacity={1}
        />
        
        <View style={[
          styles.bottomSheet,
          { paddingBottom: Math.max(insets.bottom, 40) },
        ]}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.headerTitle}>Add to calendar</Text>
            <TouchableOpacity
              style={styles.closeButton}
              onPress={onClose}
              activeOpacity={0.8}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              {CloseIcon && (
                <CloseIcon width={16} height={16} color={EVENTS_COLORS.textSecondary} />
              )}
            </TouchableOpacity>
          </View>

          {/* Calendar Options List */}
          <View style={styles.list}>
            {/* Google Calendar */}
            <TouchableOpacity
              style={styles.listItem}
              onPress={handleGoogleCalendar}
              activeOpacity={0.7}
            >
              <View style={styles.iconContainer}>
                <CalendarIcon width={20} height={20} color={EVENTS_COLORS.textSecondary} />
              </View>
              <Text style={styles.listItemText}>Google calendar</Text>
            </TouchableOpacity>

            {/* Microsoft Outlook */}
            <TouchableOpacity
              style={styles.listItem}
              onPress={handleDownloadICS}
              activeOpacity={0.7}
            >
              <View style={styles.iconContainer}>
                <CalendarIcon width={20} height={20} color={EVENTS_COLORS.textSecondary} />
              </View>
              <Text style={styles.listItemText}>Microsoft outlook</Text>
            </TouchableOpacity>

            {/* Apple iCalendar */}
            <TouchableOpacity
              style={styles.listItem}
              onPress={handleDownloadICS}
              activeOpacity={0.7}
            >
              <View style={styles.iconContainer}>
                {AppleIcon ? (
                  <AppleIcon width={20} height={20} color={EVENTS_COLORS.textSecondary} />
                ) : (
                  <CalendarIcon width={20} height={20} color={EVENTS_COLORS.textSecondary} />
                )}
              </View>
              <Text style={styles.listItemText}>Apple iCalendar</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  bottomSheet: {
    backgroundColor: EVENTS_COLORS.borderColor, // #464646 as per Figma
    borderTopLeftRadius: 32,
    borderTopRightRadius: 32,
    paddingTop: 24,
    paddingHorizontal: 24,
    width: SCREEN_WIDTH,
    maxWidth: 375,
    alignSelf: 'center',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  headerTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: '600',
    color: EVENTS_COLORS.textPrimary,
    lineHeight: 27,
  },
  closeButton: {
    width: 20,
    height: 27,
    alignItems: 'center',
    justifyContent: 'center',
  },
  list: {
    gap: 0,
  },
  listItem: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 40,
    gap: 8,
  },
  iconContainer: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  listItemText: {
    fontSize: 14,
    fontWeight: '400',
    color: EVENTS_COLORS.textPrimary,
    lineHeight: 21,
  },
});

export default AddToCalendarBottomSheet;
